import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Container,
  Disc,
  FileCog,
  Loader2,
  Monitor,
  Search,
  Usb,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useRegions } from '../../hooks/useRegions';
import { useDivisions } from '../../hooks/useDivisions';
import { useResourceLimits } from '../../hooks/useResourceLimits';
import {
  useCreateProvisioningToken,
  useProvisioningTokens,
  type ProvisioningToken,
} from '../../hooks/useProvisioningTokens';
import {
  CLAIM_CODE_LENGTH,
  claimGatewayByCode,
  normalizeClaimCode,
  type AvailableGatewayInfo,
} from '../../services/gatewayClaim';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CodeBlock } from '@/components/ui/code-block';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { FirmwareImage } from '@/features/firmware';
import { DockerDeployInstructions } from './DockerDeployInstructions';
import { GatewayImageDownloads } from './GatewayImageDownloads';
import { HyperVInstallInstructions } from './HyperVInstallInstructions';
import { SetupFileDialog } from './SetupFileCard';

const NO_DIVISION = '__none__';

/** How often to re-check whether the new gateway has checked in. */
const CHECK_IN_POLL_MS = 5_000;

type Path = 'choose' | 'code' | 'container' | 'image' | 'setupFile';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  firmwareImages: FirmwareImage[] | undefined;
  /**
   * Called when the user picks the local-network path. The dialog closes and
   * the page's scan panel takes over — the results table and claim form
   * already live there, and stacking a sheet inside this dialog to show them
   * would be worse than handing off.
   */
  onChooseScan: () => void;
}

interface PathCardProps {
  icon: React.ReactNode;
  title: string;
  blurb: string;
  recommended?: boolean;
  onClick: () => void;
}

function PathCard({ icon, title, blurb, recommended, onClick }: PathCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-md border p-4 text-left transition hover:bg-accent/40',
        recommended ? 'border-sev-5/40 bg-sev-5/5' : 'border-border-subtle',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 text-sev-5">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">{title}</h4>
            {recommended ? (
              <span className="rounded bg-sev-5/15 px-1.5 py-0.5 text-xs font-medium text-sev-5">
                Easiest
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{blurb}</p>
        </div>
        <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
      </div>
    </button>
  );
}

/**
 * One flow for adding a gateway.
 *
 * Previously this was split across two modals that referred to each other by
 * position: "Add gateway" minted a provisioning token and stopped there, while
 * "Download gateway" rendered deploy commands with a token picker whose empty
 * state told you to go create one in the *other* modal. You picked the gateway
 * type twice and the recommended zero-config path was buried below the table.
 *
 * Here the path comes first and the token is a consequence of it, minted
 * without being asked about. Every path ends on the same "waiting for it to
 * check in" state.
 */
export function AddGatewayDialog({ open, onOpenChange, firmwareImages, onChooseScan }: Props) {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [path, setPath] = useState<Path>('choose');
  const [regionId, setRegionId] = useState('');
  const [divisionId, setDivisionId] = useState(NO_DIVISION);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [minted, setMinted] = useState<ProvisioningToken | null>(null);
  // A setup file's own token (slice 5), which claims gateways too.
  const [fileToken, setFileToken] = useState<ProvisioningToken | null>(null);
  const [setupFileOpen, setSetupFileOpen] = useState(false);
  const [code, setCode] = useState('');
  const claimAbort = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const { data: regions, isLoading: isLoadingRegions } = useRegions(organizationId);
  const { data: divisions, isLoading: isLoadingDivisions } = useDivisions(organizationId);
  const { data: resourceLimits } = useResourceLimits();
  const gatewayLimits = resourceLimits?.gateways;

  const createToken = useCreateProvisioningToken();

  const claimByCode = useMutation<AvailableGatewayInfo>({
    mutationFn: () => {
      claimAbort.current?.abort();
      const abort = new AbortController();
      claimAbort.current = abort;
      return claimGatewayByCode(
        normalizeClaimCode(code),
        name.trim(),
        regionId,
        divisionId === NO_DIVISION ? undefined : divisionId,
        { signal: abort.signal },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
    },
  });
  const codeComplete = normalizeClaimCode(code).length === CLAIM_CODE_LENGTH;

  // Leaving the code step stops the wait: closing or going back reads as "never
  // mind", and a claim landing afterwards would be a surprise.
  const stopClaimByCode = () => {
    claimAbort.current?.abort();
    claimAbort.current = null;
    claimByCode.reset();
  };

  // Once a token exists, watch for it to be claimed — that transition is the
  // gateway telling us it booted, found us, and provisioned itself.
  const watching = !!minted || !!fileToken;
  const { data: tokens } = useProvisioningTokens(
    organizationId,
    watching,
    watching ? CHECK_IN_POLL_MS : undefined,
  );
  const claimedWith = (id: string | undefined) => {
    const live = tokens?.find((t) => t.id === id);
    return !!live && (live.claimedAt !== null || ['claimed', 'completed'].includes(live.status.toLowerCase()));
  };
  const checkedIn = claimedWith(minted?.id) || claimedWith(fileToken?.id);

  // One region means there is nothing to choose.
  if (regions && regions.length === 1 && regionId !== regions[0].id) setRegionId(regions[0].id);

  const reset = () => {
    setPath('choose');
    setDivisionId(NO_DIVISION);
    setName('');
    setDescription('');
    setMinted(null);
    setFileToken(null);
    setSetupFileOpen(false);
    setCode('');
    stopClaimByCode();
    createToken.reset();
    setRegionId(regions && regions.length === 1 ? regions[0].id : '');
  };

  const close = () => {
    claimAbort.current?.abort();
    onOpenChange(false);
    // Let the close animation finish before the content changes under it.
    window.setTimeout(reset, 200);
  };

  const handleScan = () => {
    onChooseScan();
    close();
  };

  const handleMint = () => {
    if (!organizationId || !regionId) return;
    createToken.mutate(
      {
        organizationId,
        divisionId: divisionId === NO_DIVISION ? null : divisionId,
        regionId,
        // Only affects expiry server-side: docker tokens last 10 years so
        // Kubernetes can keep re-pulling, everything else 3 days.
        gateway_type: path === 'container' ? 'docker' : 'appliance',
        name: name || null,
        description: description || null,
      },
      { onSuccess: setMinted },
    );
  };

  const atLimit = gatewayLimits?.atLimit ?? false;

  const chooseStep = (
    <div className="space-y-3">
      <PathCard
        recommended
        icon={<Search className="w-6 h-6" />}
        title="Find it on this network"
        blurb="For a gateway that's already powered on nearby. No token to copy — we'll spot it and you name it."
        onClick={handleScan}
      />
      <PathCard
        icon={<Monitor className="w-6 h-6" />}
        title="Enter a claim code"
        blurb="For a gateway installed from an image: its screen shows a code. Works from anywhere — you don't need to be on its network."
        onClick={() => { setPath('code'); }}
      />
      <PathCard
        icon={<Usb className="w-6 h-6" />}
        title="Use a setup file"
        blurb="For an appliance that's already installed. A file on a USB drive, or an ISO attached to the VM, sets up its network and claims it."
        onClick={() => { setPath('setupFile'); }}
      />
      <PathCard
        icon={<Container className="w-6 h-6" />}
        title="Run it as a container"
        blurb="Docker, Docker Compose or Kubernetes. We'll generate ready-to-run commands."
        onClick={() => { setPath('container'); }}
      />
      <PathCard
        icon={<Disc className="w-6 h-6" />}
        title="Install from an image"
        blurb="Download an OVA or ISO for a virtual machine or bare-metal appliance."
        onClick={() => { setPath('image'); }}
      />
    </div>
  );

  // Where the gateway goes, asked the same way whether a token is minted for it
  // or it is claimed by its code.
  const placementFields = (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="ag-region">Region</Label>
        {isLoadingRegions ? (
          <Select disabled>
            <SelectTrigger id="ag-region">
              <SelectValue placeholder="Loading regions…" />
            </SelectTrigger>
          </Select>
        ) : (
          <Select value={regionId} onValueChange={setRegionId} required>
            <SelectTrigger id="ag-region">
              <SelectValue placeholder="Select a region" />
            </SelectTrigger>
            <SelectContent>
              {regions?.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ag-division">Division (optional)</Label>
        {isLoadingDivisions ? (
          <Select disabled>
            <SelectTrigger id="ag-division">
              <SelectValue placeholder="Loading divisions…" />
            </SelectTrigger>
          </Select>
        ) : (
          <Select value={divisionId} onValueChange={setDivisionId}>
            <SelectTrigger id="ag-division">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DIVISION}>None</SelectItem>
              {divisions?.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </>
  );

  const limitsAlert = gatewayLimits ? (
    <Alert
      className={cn(
        atLimit
          ? 'border-sev-1/40 bg-sev-1/10'
          : gatewayLimits.approachingLimit
            ? 'border-sev-3/40 bg-sev-3/10'
            : 'border-sev-5/30 bg-sev-5/5',
      )}
    >
      <AlertDescription>
        <strong>Gateways:</strong> {gatewayLimits.currentCount} of{' '}
        {gatewayLimits.softLimit} used
        {atLimit
          ? '. You have reached your gateway limit. Please upgrade your plan or contact support.'
          : null}
      </AlertDescription>
    </Alert>
  ) : null;

  // What the gateway is called, asked the same way for a token Add gateway
  // mints and for a setup file's own.
  const nameFields = (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="ag-name">Name (optional)</Label>
        <Input
          id="ag-name"
          type="text"
          placeholder="e.g., Main Office Gateway"
          value={name}
          onChange={(e) => { setName(e.target.value); }}
        />
        <p className="text-xs text-muted-foreground">
          Assigned to the gateway when it checks in.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ag-desc">Description (optional)</Label>
        <Textarea
          id="ag-desc"
          placeholder="Add a description"
          value={description}
          onChange={(e) => { setDescription(e.target.value); }}
        />
      </div>
    </>
  );

  const backButton = (
    <Button variant="ghost" onClick={() => { setPath('choose'); }} className="gap-1.5">
      <ArrowLeft className="w-4 h-4" />
      Back
    </Button>
  );

  const configureStep = (
    <div className="space-y-4">
      {createToken.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{createToken.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {placementFields}
      {nameFields}
      {limitsAlert}

      <div className="flex justify-between gap-2">
        {backButton}
        <Button
          onClick={handleMint}
          disabled={!regionId || createToken.isPending || atLimit}
        >
          {createToken.isPending ? 'Preparing…' : 'Continue'}
        </Button>
      </div>
    </div>
  );

  const codeStep = (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        claimByCode.mutate();
      }}
    >
      {claimByCode.isError && claimByCode.error.name !== 'AbortError' ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{claimByCode.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="ag-code">Claim code</Label>
        <Input
          id="ag-code"
          type="text"
          className="font-mono"
          placeholder="abcd-efgh-jkmn"
          autoComplete="off"
          spellCheck={false}
          value={code}
          onChange={(e) => { setCode(e.target.value); }}
        />
        <p className="text-xs text-muted-foreground">
          On the gateway&apos;s screen, under its address. Dashes and capitals don&apos;t matter.
        </p>
      </div>

      {placementFields}

      <div className="space-y-1.5">
        <Label htmlFor="ag-code-name">Name</Label>
        <Input
          id="ag-code-name"
          type="text"
          placeholder="e.g., Main Office Gateway"
          value={name}
          onChange={(e) => { setName(e.target.value); }}
          required
        />
      </div>

      {limitsAlert}

      {claimByCode.isPending ? (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertDescription>
            Looking for the gateway showing this code… usually a few seconds, or a minute or
            two if the gateway has only just started.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            stopClaimByCode();
            setPath('choose');
          }}
          className="gap-1.5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={!codeComplete || !regionId || !name.trim() || claimByCode.isPending || atLimit}
        >
          {claimByCode.isPending ? 'Claiming…' : 'Claim gateway'}
        </Button>
      </div>
    </form>
  );

  const codeClaimedStep = (
    <div className="space-y-4">
      <Alert className="border-sev-4/40 bg-sev-4/10">
        <CheckCircle2 className="h-4 w-4 text-sev-4" />
        <AlertDescription>
          <strong>Gateway claimed.</strong> It&apos;s provisioning itself now and will appear
          in your gateway list.
        </AlertDescription>
      </Alert>
      <div className="flex justify-end">
        <Button variant="outline" onClick={close}>Done</Button>
      </div>
    </div>
  );

  const checkInBanner = checkedIn ? (
    <Alert className="border-sev-4/40 bg-sev-4/10">
      <CheckCircle2 className="h-4 w-4 text-sev-4" />
      <AlertDescription>
        <strong>Gateway checked in.</strong> It&apos;s provisioning itself now and will
        appear in your gateway list.
      </AlertDescription>
    </Alert>
  ) : (
    <Alert>
      <Loader2 className="h-4 w-4 animate-spin" />
      <AlertDescription>
        Waiting for the gateway to check in… leave this open and it&apos;ll update here.
      </AlertDescription>
    </Alert>
  );

  // The setup-file form, over this dialog. The file's token is its own, made on
  // its first download, so the setup-file path mints none here; reopened, the
  // form makes more files with the same token.
  const setupFileDialog = setupFileOpen && organizationId ? (
    <SetupFileDialog
      target={{
        newGateways: {
          organizationId,
          regionId,
          divisionId: divisionId === NO_DIVISION ? null : divisionId,
          name: name || null,
          description: description || null,
        },
        token: fileToken ?? undefined,
        onToken: setFileToken,
      }}
      onClose={() => { setSetupFileOpen(false); }}
    />
  ) : null;

  // For an appliance already installed: no image to download and no token of
  // Add gateway's own, just the file (ruling 2026-10-30).
  const setupFileStep = fileToken ? (
    <div className="space-y-4">
      {checkInBanner}
      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={() => { setSetupFileOpen(true); }}>
          Make another file
        </Button>
        <Button variant="outline" onClick={close}>
          {checkedIn ? 'Done' : 'Close'}
        </Button>
      </div>
      {setupFileDialog}
    </div>
  ) : (
    <div className="space-y-4">
      {placementFields}
      {nameFields}
      {limitsAlert}

      <div className="flex justify-between gap-2">
        {backButton}
        <Button onClick={() => { setSetupFileOpen(true); }} disabled={!regionId || atLimit}>
          Make the setup file
        </Button>
      </div>
      {setupFileDialog}
    </div>
  );

  // min-w-0: the dialog lays its children out on a grid, whose items will not
  // shrink below their widest unbreakable line. The Hyper-V one-liner is one
  // such line, ~600 characters, and widened the whole dialog past its edge.
  const readyStep = minted ? (
    <div className="min-w-0 space-y-4">
      {checkInBanner}

      {path === 'container' ? (
        <DockerDeployInstructions tokens={[]} token={minted.token} />
      ) : (
        <GatewayImageDownloads
          firmwareImages={firmwareImages}
          installSlot={<HyperVInstallInstructions token={minted.token} />}
          tokenSlot={
            <div className="space-y-1.5">
              <Label>Provisioning token</Label>
              <CodeBlock code={minted.token} />
              <p className="text-xs text-muted-foreground">
                Once the gateway is running, its screen shows a claim code: close this and
                choose <span className="font-medium">Enter a claim code</span>, from anywhere.
                Or, from the gateway&apos;s own network, open{' '}
                <code className="font-mono">http://&lt;its address&gt;:8080</code> (its screen
                shows the address) and paste this token. The token expires in 3 days; run Add
                gateway again for a new one.
              </p>
            </div>
          }
        />
      )}

      {path !== 'container' && organizationId ? (
        <div className="rounded-md border border-border-subtle bg-card p-3 space-y-2" data-testid="add-setup-file">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FileCog className="w-4 h-4 text-muted-foreground" />
            Setup file
          </div>
          <p className="text-xs text-muted-foreground">
            No DHCP, VLANs, or a proxy in the way? A setup file on a USB drive, or an ISO attached to the VM, sets the
            network up at first boot and claims the gateway with a token of its own.
          </p>
          <Button size="sm" variant="outline" onClick={() => { setSetupFileOpen(true); }}>
            Make a setup file
          </Button>
          {setupFileDialog}
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button variant="outline" onClick={close}>
          {checkedIn ? 'Done' : 'Close'}
        </Button>
      </div>
    </div>
  ) : null;

  const title =
    path === 'setupFile'
      ? fileToken
        ? 'Use your setup file'
        : 'Where will it live?'
      : path === 'choose'
        ? 'Add a gateway'
        : path === 'code'
          ? claimByCode.isSuccess
            ? 'Gateway claimed'
            : 'Enter a claim code'
          : minted
            ? path === 'container'
              ? 'Run your gateway'
              : 'Install your gateway'
            : 'Where will it live?';

  const subtitle =
    path === 'setupFile'
      ? fileToken
        ? 'Plug the drive into the appliance, or attach the ISO to its VM, whether it’s running or not.'
        : 'We’ll put a token in the file to claim it with.'
      : path === 'choose'
        ? 'Gateways connect this SaaS to your network. How do you want to add one?'
        : path === 'code'
          ? 'Type the code from the gateway’s screen. You don’t need to be on its network.'
          : minted
            ? path === 'container'
              ? 'Run these where the gateway should live. The token is already filled in.'
              : 'Install the image, then claim it with the code on its screen, or on its own page with the token below.'
            : path === 'container'
              ? 'We’ll issue its credentials and hand you ready-to-run commands.'
              : 'We’ll issue a token to claim it with.';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        {path === 'setupFile'
          ? setupFileStep
          : path === 'choose'
            ? chooseStep
            : path === 'code'
              ? claimByCode.isSuccess
                ? codeClaimedStep
                : codeStep
              : minted
                ? readyStep
                : configureStep}
      </DialogContent>
    </Dialog>
  );
}
